import React, { useEffect, useState } from 'react'
import {
  AlertCircle,
  Boxes,
  ExternalLink,
  FileImage,
  Layers,
  Loader2,
  ShoppingBag,
} from 'lucide-react'
import { Badge } from '../../components/ui/Badge'
import { cn } from '../../lib/cn'
import { assertNever, MARKETPLACES, MARKETPLACE_BADGE_TONE } from '../../shared/marketplaces'
import { entityRelationsAPI } from './api'
import type {
  CurrentRelationEntity,
  EntityRelations,
  RelationCard,
  RelationComponent,
  RelationProduct,
} from './types'

interface EntityRelationsPanelProps {
  article?: string | null
  designCode?: string | number | null
  current?: CurrentRelationEntity
}

const statusLabels: Record<string, string> = {
  active: 'Активна',
  archived: 'Архив',
  pending: 'Ожидает',
  ready: 'Готова',
  selling: 'Историческая отметка PIM',
  created: 'Создана',
  error: 'Ошибка',
  synced: 'Синхронизирован',
  pending_1c: 'Ожидает 1С',
}

const relationCardClass = (active: boolean) =>
  cn(
    'rounded-lg border bg-white p-3 dark:bg-zinc-900',
    active
      ? 'border-indigo-400 ring-2 ring-indigo-100 dark:border-indigo-500 dark:ring-indigo-950'
      : 'border-gray-200 dark:border-zinc-800',
  )

function cardLabel(card: RelationCard): string {
  switch (card.marketplace) {
    case 'wb':
      return `NM ${card.id}`
    case 'ozon':
      return card.externalId || `Ozon #${card.id}`
    case 'ym':
      // market_sku назначает модерация Маркета; до этого момента внешнего
      // идентификатора нет (SPEC_2026-09-13_YANDEX_MARKET, §3.4).
      return card.externalId || `Я.Маркет #${card.id}`
    default:
      return assertNever(card.marketplace)
  }
}

const MarketplaceCard: React.FC<{
  card: RelationCard
  current?: CurrentRelationEntity
}> = ({ card, current }) => {
  const active = current?.kind === card.marketplace && current.id === card.id
  const body = (
    <div className={relationCardClass(active)}>
      <div className="flex items-start gap-2">
        <Badge variant={MARKETPLACE_BADGE_TONE[card.marketplace]}>
          {MARKETPLACES[card.marketplace].label}
        </Badge>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-mono text-xs font-semibold text-gray-900 dark:text-zinc-100">
              {cardLabel(card)}
            </span>
            {card.url && <ExternalLink className="h-3 w-3 shrink-0 text-gray-400" />}
          </div>
          {card.title && (
            <div className="mt-0.5 truncate text-xs text-gray-600 dark:text-zinc-300">
              {card.title}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-gray-500 dark:text-zinc-400">
            <span>{card.sellerName || `Кабинет #${card.sellerId}`}</span>
            <span>·</span>
            <span>{card.archived ? 'Архив' : statusLabels[card.status || ''] || card.status || 'Без статуса'}</span>
          </div>
        </div>
      </div>
    </div>
  )

  return card.url ? (
    <a href={card.url} target="_blank" rel="noreferrer" className="block">
      {body}
    </a>
  ) : (
    body
  )
}

const ProductNode: React.FC<{
  product: RelationProduct
  current?: CurrentRelationEntity
}> = ({ product, current }) => {
  const active = current?.kind === 'product' && current.id === product.article
  return (
    <section className={relationCardClass(active)}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300">
          <Boxes className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <a
            href={`/products?article=${encodeURIComponent(product.article)}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
          >
            {product.article}
            <ExternalLink className="h-3 w-3" />
          </a>
          <div className="mt-0.5 truncate text-xs text-gray-600 dark:text-zinc-300">
            {product.title || 'Без названия'}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {product.category && <Badge variant="gray">{product.category}</Badge>}
            {product.oneCStatus && (
              <Badge variant={product.oneCStatus === 'synced' ? 'green' : 'yellow'}>
                {statusLabels[product.oneCStatus] || product.oneCStatus}
              </Badge>
            )}
            {product.archived && <Badge variant="red">Архив</Badge>}
          </div>
        </div>
        <div className="text-right text-2xs text-gray-500 dark:text-zinc-400">
          <div>{MARKETPLACES.wb.label}: {product.wbCards.length}</div>
          <div>{MARKETPLACES.ozon.label}: {product.ozonCards.length}</div>
          <div>{MARKETPLACES.ym.label}: {product.ymCards.length}</div>
        </div>
      </div>

      {((product.components?.length ?? 0) > 0 || (product.usedIn?.length ?? 0) > 0) && (
        <div className="mt-3 border-l-2 border-gray-100 pl-3 dark:border-zinc-800">
          <ComponentsBlock components={product.components ?? []} usedIn={product.usedIn ?? []} />
        </div>
      )}

      <div className="mt-3 border-l-2 border-gray-100 pl-3 dark:border-zinc-800">
        {product.wbCards.length === 0 && product.ozonCards.length === 0 && product.ymCards.length === 0 ? (
          <p className="py-1 text-xs text-gray-500 dark:text-zinc-400">
            Карточки маркетплейсов пока не связаны.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            {[...product.wbCards, ...product.ozonCards, ...product.ymCards].map((card) => (
              <MarketplaceCard
                key={`${card.marketplace}-${card.sellerId}-${card.id}`}
                card={card}
                current={current}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

const ComponentRow: React.FC<{ entry: RelationComponent; withQuantity: boolean }> = ({ entry, withQuantity }) => (
  <li className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs dark:border-zinc-800 dark:bg-zinc-900">
    {withQuantity && (
      <span className="w-9 shrink-0 font-mono text-gray-500 dark:text-zinc-400">{entry.quantity} ×</span>
    )}
    <a
      href={`/products?article=${encodeURIComponent(entry.article)}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex shrink-0 items-center gap-1 font-mono font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
    >
      {entry.article}
      <ExternalLink className="h-3 w-3" />
    </a>
    <span className="min-w-0 truncate text-gray-600 dark:text-zinc-300">{entry.title || 'Без названия'}</span>
  </li>
)

/** Состав набора (PP1 → PP0) и наборы, куда входит товар: из `components` ответа. */
const ComponentsBlock: React.FC<{ components: RelationComponent[]; usedIn: RelationComponent[] }> = ({ components, usedIn }) => (
  <div className="space-y-3">
    {components.length > 0 && (
      <section aria-label="Состав">
        <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-zinc-100">
          <Layers className="h-3.5 w-3.5 text-gray-400" />
          Состав
          <span className="font-normal text-gray-500 dark:text-zinc-400">· {components.length}</span>
        </h4>
        <ul className="space-y-1">
          {components.map((entry, index) => (
            <ComponentRow key={`${entry.article}-${entry.position ?? index}`} entry={entry} withQuantity />
          ))}
        </ul>
      </section>
    )}
    {usedIn.length > 0 && (
      <section aria-label="Входит в наборы">
        <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-zinc-100">
          <Layers className="h-3.5 w-3.5 text-gray-400" />
          Входит в наборы
          <span className="font-normal text-gray-500 dark:text-zinc-400">· {usedIn.length}</span>
        </h4>
        <ul className="space-y-1">
          {usedIn.map((entry) => (
            <ComponentRow key={entry.article} entry={entry} withQuantity={false} />
          ))}
        </ul>
      </section>
    )}
  </div>
)

export const EntityRelationsPanel: React.FC<EntityRelationsPanelProps> = ({
  article,
  designCode,
  current,
}) => {
  const [relations, setRelations] = useState<EntityRelations | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!article && !designCode) {
      setRelations(null)
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    void entityRelationsAPI
      .get({
        article: article ? String(article) : undefined,
        designCode: designCode != null ? String(designCode) : undefined,
        signal: controller.signal,
      })
      .then(setRelations)
      .catch((requestError: any) => {
        if (requestError?.name === 'CanceledError' || requestError?.name === 'AbortError') return
        setError(
          requestError?.response?.data?.error?.message ||
            requestError?.message ||
            'Не удалось загрузить связи',
        )
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [article, designCode])

  if (loading) {
    return (
      <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Загрузка связей…
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        {error}
      </div>
    )
  }

  if (!relations?.design) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-zinc-700">
        <ShoppingBag className="mx-auto h-6 w-6 text-gray-300 dark:text-zinc-600" />
        <p className="mt-2 text-sm font-medium text-gray-700 dark:text-zinc-200">
          Цепочка связей не найдена
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          У записи нет доступной связи с макетом и товаром PIM.
        </p>
      </div>
    )
  }

  const designActive =
    current?.kind === 'design' && current.id === String(relations.design.designCode)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            Цепочка происхождения
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
            Макет → товары PIM → карточки маркетплейсов
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="indigo">Товаров: {relations.totals.products}</Badge>
          <Badge variant={MARKETPLACE_BADGE_TONE.wb}>{MARKETPLACES.wb.label}: {relations.totals.wbCards}</Badge>
          <Badge variant={MARKETPLACE_BADGE_TONE.ozon}>{MARKETPLACES.ozon.label}: {relations.totals.ozonCards}</Badge>
          <Badge variant={MARKETPLACE_BADGE_TONE.ym}>{MARKETPLACES.ym.label}: {relations.totals.ymCards}</Badge>
        </div>
      </div>

      <div className={relationCardClass(designActive)}>
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300">
            <FileImage className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <a
              href={`/designs?design=${encodeURIComponent(relations.design.designCode)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
            >
              Макет #{relations.design.designCode}
              <ExternalLink className="h-3 w-3" />
            </a>
            <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">
              {[relations.design.type, relations.design.category, relations.design.brand]
                .filter(Boolean)
                .join(' · ') || 'Атрибуты не заполнены'}
            </div>
            {(relations.design.theme || relations.design.themeNumber) && (
              <div className="mt-1 text-2xs text-gray-500 dark:text-zinc-400">
                Тематика: {relations.design.theme || `№ ${relations.design.themeNumber}`}
              </div>
            )}
          </div>
          {relations.design.archived && <Badge variant="red">Архив</Badge>}
        </div>
      </div>

      <div className="ml-4 border-l-2 border-indigo-100 pl-4 dark:border-indigo-950">
        {relations.products.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-5 text-center text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
            У макета пока нет производных товаров.
          </div>
        ) : (
          <div className="space-y-2.5">
            {relations.products.map((product) => (
              <ProductNode key={product.article} product={product} current={current} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default EntityRelationsPanel
